import axios from 'axios';
import { useEffect, useRef } from 'react';
import { SearchCandidateProfileMessageSender } from 'generated-graphql/types';
import { SearchCandidateVectorQueryMessage } from 'modules/candidate-profile/types/SearchCandidateVectorQueryMessage';
import {
  getTalentPoolCondition,
  removableFields,
  withoutTalentPoolFilter
} from 'modules/candidate-profile/tables/filters/getTalentPoolCondition';
import { useOrganizationCustomFieldConfigs } from 'modules/candidate-profile/hooks/useOrganizationCustomFieldConfigs';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { logger } from 'core/utils/logger';
import { TableV3Filters } from 'common/table/components/TableV3';
import { MergeAppliedFilters } from 'modules/candidate-profile/hooks/useCandidateFilterConditions';
import { useVectorSearchCache } from 'modules/candidate-profile/hooks/useVectorSearchCache';
import { useCandidateListStore } from 'modules/candidate-profile/stores/useCandidateListStore';
import { CandidateProfileListProps } from 'modules/candidate-profile/types/CandidateProfileListProps';
import {
  CandidateFilterGroups,
  CandidateFilterNode,
  CandidateFilterState,
  SourcingReplyRequest
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import {
  buildReplyRequest,
  useSourcingReply
} from 'modules/candidate-profile/hooks/useSourcingReply';
import { getCandidateBatchFromCandidateProfileListProps } from 'modules/candidate-profile/utils/getCandidateBatchFromCandidateProfileListProps';
import { shouldShowConversation } from 'modules/candidate-profile/utils/shouldShowConversation';
import { useBiasConfirmation } from 'modules/candidate-profile/hooks/useBiasConfirmation';
import { useSearchTimeoutAlert } from 'modules/candidate-profile/hooks/useSearchTimeoutAlert';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast/types';
import { buildSourcingSearchRequest } from 'modules/candidate-profile/tables/filters/buildSourcingSearchRequest';

// Above the endpoint's own 90s, so its answer -- result or error -- wins the
// race. This is the backstop for a connection that never returns at all.
const SEARCH_TIMEOUT_MS = 95_000;

/**
 * The filter tree a search ran on: what this client sent (`appliedFilter`)
 * AND what the assistant derived from the message (`generatedFilter`).
 */
const combineFilterTrees = (
  applied: CandidateFilterNode | null | undefined,
  generated: CandidateFilterNode | null | undefined
): CandidateFilterNode | null => {
  if (!applied) return generated ?? null;
  if (!generated) return applied;

  return { and: [applied, generated] };
};

const TIMEOUT_SENTINEL = Symbol('search-timeout');
type TimeoutSentinel = typeof TIMEOUT_SENTINEL;

type ExecuteSearchParams = {
  searchQueryId: string | null;
  placeholderMessage: SearchCandidateVectorQueryMessage;
  removeFields: string[];
};

type ExecuteSearchResult = {
  userMessage?: SearchCandidateVectorQueryMessage;
  replyRequest?: SourcingReplyRequest;
  candidateProfileIds?: string[];
  searchId?: string;
  newSearchQueryId?: string | null;
  // Set when the endpoint refused the query; nothing was searched or saved.
  biasMessage?: string;
};

type UseVectorSearchAndReplyProps = {
  candidateProfileListProps: CandidateProfileListProps;
  filters: TableV3Filters<CandidateProfileTableColumns> | undefined;
  filterConditions: CandidateFilterState[];
  filterGroups: CandidateFilterGroups;
  mergeAppliedFilters: MergeAppliedFilters;
  searchQueryId: string | null;
  updateSearchQueryId: (id: string) => void;
  setPlaceholder: (message: SearchCandidateVectorQueryMessage | null) => void;
  addMessages: (messages: SearchCandidateVectorQueryMessage[]) => void;
  /** The search in the URL has not loaded yet, so its chips are not here. */
  awaitingSavedSearch: boolean;
};

/**
 * Manages vector search execution and AI reply generation.
 * Coordinates the flow: search for candidates -> generate AI reply.
 */
export const useVectorSearchAndReply = ({
  candidateProfileListProps,
  filters,
  filterConditions,
  filterGroups,
  mergeAppliedFilters,
  searchQueryId,
  updateSearchQueryId,
  setPlaceholder,
  addMessages,
  awaitingSavedSearch
}: UseVectorSearchAndReplyProps) => {
  const shouldShowConversationValue = shouldShowConversation(
    candidateProfileListProps
  );
  const {
    data,
    explanationContext,
    similarCandidateNames,
    loading,
    handleSourcingSearch,
    refetchVectorSearch,
    associateCachedResultWithRequest,
    clearSearchCache
  } = useVectorSearchCache();
  const { setTotalCandidatesCount, setIsCandidatesCountLoading } =
    useCandidateListStore();

  const { showBiasedSearchBlocked } = useBiasConfirmation();
  const { showSearchTimeoutAlert } = useSearchTimeoutAlert();
  const { showToast } = useToast();
  const { customFieldConfigs, loading: customFieldConfigsLoading } =
    useOrganizationCustomFieldConfigs();

  const { isReplying, requestReply } = useSourcingReply({ addMessages });

  // Track if component is mounted to prevent URL updates after navigation
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      setIsCandidatesCountLoading(false);
    };
  }, [setIsCandidatesCountLoading]);

  const candidateProfiles = data?.candidateProfiles ?? [];
  const totalCandidatesCount = data?.totalCandidatesCount;

  useEffect(() => {
    // A search that finds no one reports 0; skipping it kept the last total.
    if (totalCandidatesCount !== undefined) {
      setTotalCandidatesCount(totalCandidatesCount);
    }
  }, [totalCandidatesCount]);

  // The request last sent, so an effect run that would repeat it does not.
  const prevRequestRef = useRef<string>();
  // The chips as of the latest render, so a turn that resolves can tell
  // whether the recruiter changed them while it was in flight.
  const latestConditionsRef = useRef(filterConditions);
  latestConditionsRef.current = filterConditions;
  const latestGroupsRef = useRef(filterGroups);
  latestGroupsRef.current = filterGroups;
  // The conditions the last turn's filters were merged into, so the effect can
  // tell them from a change the recruiter made.
  const mergedConditionsRef = useRef<CandidateFilterState[] | null>(null);

  // With none of these it is a browse, which the endpoint serves as the
  // whole list.
  const tableConditions = () => {
    const talentPool = getTalentPoolCondition(candidateProfileListProps);

    return [...(talentPool ? [talentPool] : []), ...filterConditions];
  };

  // Passed in, not read from state: mid-search, state still holds the last render.
  const buildTableRequest = (
    searchId: string | null,
    candidatesToMatch: string[] = similarCandidateNames
  ) =>
    buildSourcingSearchRequest({
      filters: tableConditions(),
      groups: filterGroups,
      customFieldConfigs,
      ...(searchId ? { searchId } : {}),
      likeCandidateNames: candidatesToMatch,
      skipReply: true
    });

  // When search should run in this effect:
  // 1. Initial mount
  // 2. When filters update
  useEffect(() => {
    const callVectorSearch = async () => {
      if (loading) return;
      if (!filters) return; // Wait for TableV3 to initialize filters
      if (customFieldConfigsLoading) return; // availableFilters is built from these
      // Sent now, the request would carry the id and none of its filters.
      if (awaitingSavedSearch) return;

      const candidateBatchId = getCandidateBatchFromCandidateProfileListProps(
        candidateProfileListProps
      );

      // Skip initial load if we have a candidateBatchId but no searchQueryId yet
      // This means we're waiting for the auto-message to be sent
      // Only skip if conversation is shown (otherwise there's no auto-message)
      if (candidateBatchId && !searchQueryId && shouldShowConversationValue)
        return;

      // Ignore searchQueryId when conversation is not shown
      const effectiveSearchQueryId = shouldShowConversationValue
        ? searchQueryId
        : null;

      const request = buildTableRequest(effectiveSearchQueryId);
      const signature = JSON.stringify(request);

      // These chips are the search that just ran, not a change to it. Record
      // what would have been sent -- so a later edit still reads as a change --
      // and skip the round trip. Matching on the array the merge committed
      // rather than a flag: anything the recruiter does next is a new array, so
      // a real change can never be swallowed here.
      if (mergedConditionsRef.current === filterConditions) {
        mergedConditionsRef.current = null;
        prevRequestRef.current = signature;
        associateCachedResultWithRequest(request);
        return;
      }

      if (prevRequestRef.current === signature) return;
      prevRequestRef.current = signature;

      setIsCandidatesCountLoading(true);

      try {
        const result = await handleSourcingSearch(request);

        // Don't update URL if component unmounted (user navigated away)
        if (!isMountedRef.current) return;

        const id = result.data?.id;

        if (id && !searchQueryId && shouldShowConversationValue) {
          // The id rides in the request, so the effect is about to re-run with
          // a signature it has never sent and fetch these same rows again.
          // Record what it would build, as the chat turn does with its own id.
          const requestWithId = buildTableRequest(id);
          prevRequestRef.current = JSON.stringify(requestWithId);
          associateCachedResultWithRequest(requestWithId);
          updateSearchQueryId(id);
        }
      } catch (error) {
        logger.error('Candidate filter search failed', { error });

        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        const isRejected =
          status !== undefined && status >= 400 && status < 500;

        // Forget it, or re-applying the same filter looks unchanged and
        // never retries. A rejected one would just fail again.
        if (!isRejected) {
          prevRequestRef.current = undefined;
        }

        if (isMountedRef.current) {
          showToast({
            title: 'Could not apply those filters',
            type: ToastType.Error
          });
        }
      } finally {
        setIsCandidatesCountLoading(false);
      }
    };

    callVectorSearch();
  }, [
    filters,
    filterConditions,
    filterGroups,
    searchQueryId,
    loading,
    customFieldConfigsLoading,
    candidateProfileListProps,
    awaitingSavedSearch
  ]);

  const executeSearch = async ({
    searchQueryId,
    placeholderMessage,
    removeFields
  }: ExecuteSearchParams): Promise<ExecuteSearchResult | null> => {
    const conditionsAtSend = filterConditions;
    const groupsAtSend = filterGroups;

    // The chips still hold these until the reply lands, so they are taken out
    // here: the turn must not search on what it asks to drop.
    const { response, superseded } = await handleSourcingSearch(
      buildSourcingSearchRequest({
        filters: tableConditions().filter(
          (condition) => !removeFields.includes(condition.field)
        ),
        groups: groupsAtSend,
        customFieldConfigs,
        ...(searchQueryId ? { searchId: searchQueryId } : {}),
        messageBody: placeholderMessage.body
      })
    );

    if (!response) return null;

    // A flagged query never searched, so there is nothing to add to the
    // conversation -- the caller shows the notice and stops.
    if (response.bias?.query_is_biased) {
      return { biasMessage: response.bias.message ?? '' };
    }

    const newSearchId = response.id || searchQueryId || null;

    // Chips changed in flight were never searched, so the effect has to run.
    const rowsMatchChips =
      !superseded &&
      latestConditionsRef.current === conditionsAtSend &&
      latestGroupsRef.current === groupsAtSend;

    const mergedConditions = mergeAppliedFilters(
      withoutTalentPoolFilter(
        combineFilterTrees(response.appliedFilter, response.generatedFilter),
        candidateProfileListProps
      ),
      // The agent can drop a field too; keep it off the chips.
      [...removeFields, ...(response.removedFilterFields ?? [])],
      conditionsAtSend,
      groupsAtSend
    );
    mergedConditionsRef.current = rowsMatchChips ? mergedConditions : null;

    // Without this the new search id reads as a change and the effect runs
    // over the top of what the chat just returned. With nothing merged the
    // rows hold a filter no chip shows, so the effect searches again for the
    // chips alone.
    prevRequestRef.current =
      mergedConditions && !superseded
        ? JSON.stringify(
            buildTableRequest(newSearchId, response.likeCandidateNames ?? [])
          )
        : undefined;

    const newUserMessage = response.newUserMessage;
    if (!response.id || !newUserMessage) {
      return null;
    }

    const id = response.id;
    const candidateProfileIds = (response.candidates ?? []).map(
      (candidate) => candidate.candidate_profile_id as string
    );

    return {
      userMessage: {
        id: newUserMessage.id,
        body: newUserMessage.body,
        sender: newUserMessage.sender as SearchCandidateProfileMessageSender,
        date: newUserMessage.createdAt ?? new Date().toISOString()
      },
      replyRequest: buildReplyRequest(
        response,
        id,
        newUserMessage.id,
        placeholderMessage.body
      ),
      candidateProfileIds,
      searchId: id,
      newSearchQueryId: !searchQueryId ? id : null
    };
  };

  const handlePlaceholderMessage = async (
    placeholderMessage: SearchCandidateVectorQueryMessage,
    requestedRemoveFields: string[] = []
  ): Promise<void> => {
    const removeFields = removableFields(
      requestedRemoveFields,
      candidateProfileListProps
    );

    setPlaceholder(placeholderMessage);

    // The endpoint screens the message and answers a brief it cannot search
    // with clarifying questions, so there is no separate check to run first.

    const runSearchWithTimeout = async (): Promise<
      ExecuteSearchResult | TimeoutSentinel | null
    > => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<TimeoutSentinel>((resolve) => {
        timeoutId = setTimeout(
          () => resolve(TIMEOUT_SENTINEL),
          SEARCH_TIMEOUT_MS
        );
      });
      try {
        return await Promise.race([
          executeSearch({ searchQueryId, placeholderMessage, removeFields }),
          timeoutPromise
        ]);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    let result = await runSearchWithTimeout();
    while (result === TIMEOUT_SENTINEL) {
      if (!isMountedRef.current) return;
      const retry = await showSearchTimeoutAlert();
      if (!isMountedRef.current) return;
      if (!retry) {
        setPlaceholder(null);
        return;
      }
      result = await runSearchWithTimeout();
    }

    if (!isMountedRef.current) return;
    if (!result) {
      // Search failed (e.g. network error). Clear the placeholder so the
      // user is not left with a stuck loading bubble.
      setPlaceholder(null);
      return;
    }

    if (result.biasMessage !== undefined) {
      showBiasedSearchBlocked(result.biasMessage);
      setPlaceholder(null);
      return;
    }

    if (!result.userMessage) {
      setPlaceholder(null);
      return;
    }

    addMessages([result.userMessage]);

    if (result.newSearchQueryId) {
      updateSearchQueryId(result.newSearchQueryId);
    }

    if (result.replyRequest) {
      await requestReply(result.replyRequest);
    }
  };

  return {
    candidateProfiles,
    explanationContext,
    hasSearchResult: data !== undefined,
    isSearching: loading,
    isReplying,
    clearSearchCache,
    handlePlaceholderMessage,
    refetchVectorSearch
  };
};
